<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export default {
  name: "Navigation",
  setup() {
    const scrolledNav = ref(false);
    const mobile = ref(false);
    const mobileNav = ref(false);
    const navLoaded = ref(false);
    const router = useRouter();
    const route = useRoute();

    const toggleMobileNav = () => mobileNav.value = !mobileNav.value;
    const updateScroll = () => scrolledNav.value = window.scrollY > 50;
    const checkScreen = () => {
      mobile.value = window.innerWidth <= 750;
      if (!mobile.value) mobileNav.value = false;
    };

    const handleNavigation = (event, sectionId) => {
      event.preventDefault();
      if (mobileNav.value) mobileNav.value = false;
      if (route.path === '/') {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        router.push({ path: '/', hash: `#${sectionId}` }).then(() => {
          setTimeout(() => {
            document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
        });
      }
    };

    onMounted(() => {
      window.addEventListener('resize', checkScreen);
      window.addEventListener('scroll', updateScroll);
      checkScreen();
      setTimeout(() => navLoaded.value = true, 600);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', checkScreen);
      window.removeEventListener('scroll', updateScroll);
    });

    return { scrolledNav, mobile, mobileNav, toggleMobileNav, navLoaded, handleNavigation };
  },
};
</script>

<template>
  <header :class="{ 'scrolled-nav': scrolledNav }">
    <nav>
      <a class="logo" href="#home" @click="handleNavigation($event, 'home')">NS</a>

      <ul v-show="!mobile" :class="['navigation', { 'nav-loaded': navLoaded }]">
        <li v-for="[id, label] in [['about','About'],['projects','Projects'],['contact','Contact']]" :key="id">
          <a class="link" :href="`#${id}`" @click="handleNavigation($event, id)">{{ label }}</a>
        </li>
      </ul>

      <div class="icon" v-if="mobile" @click="toggleMobileNav">
        <span :class="['hamburger', { open: mobileNav }]">
          <span /><span /><span />
        </span>
      </div>

      <transition name="mobile-nav">
        <ul v-if="mobileNav" class="dropdown-nav">
          <li v-for="[id, label] in [['about','About'],['projects','Projects'],['contact','Contact']]" :key="id">
            <a class="link" :href="`#${id}`" @click="handleNavigation($event, id)">{{ label }}</a>
          </li>
        </ul>
      </transition>
    </nav>
  </header>
</template>

<style scoped>
header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1.2rem 2rem;
  transition: background 0.3s, backdrop-filter 0.3s;
}

header.scrolled-nav {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

nav {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Logo */
.logo {
  font-size: 1.4rem;
  font-weight: 900;
  letter-spacing: -1px;
  text-decoration: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Desktop nav */
.navigation {
  display: flex;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  opacity: 0;
  transform: translateY(-20px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.nav-loaded {
  opacity: 1;
  transform: translateY(0);
}

.link {
  text-decoration: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: color 0.3s;
  position: relative;
}

.link::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 1px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  transition: width 0.3s;
}

.link:hover { color: white }
.link:hover::after { width: 100% }

/* Hamburger */
.icon { cursor: pointer }

.hamburger {
  display: flex;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
}

.hamburger span {
  width: 24px;
  height: 2px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 2px;
  transition: all 0.3s;
  display: block;
}

.hamburger.open span:nth-child(1) { transform: rotate(45deg) translate(5px, 7px) }
.hamburger.open span:nth-child(2) { opacity: 0 }
.hamburger.open span:nth-child(3) { transform: rotate(-45deg) translate(5px, -7px) }

/* Mobile dropdown */
.dropdown-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 70%;
  height: 100vh;
  background: rgba(15, 12, 41, 0.97);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 300;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.dropdown-nav .link {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 1px;
}

.dropdown-nav .link:hover { color: #667eea }

/* Transition */
.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}
</style>